import React, { useMemo } from 'react';

function fmtSecs(secs) {
  if (!secs && secs !== 0) return '—';
  const s = Math.round(secs);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${s}s`;
}

function scoreClass(correct, total) {
  if (!total) return '';
  const pct = correct / total;
  if (pct >= 0.7) return 'qp-summary-topic-green';
  if (pct >= 0.5) return 'qp-summary-topic-amber';
  return 'qp-summary-topic-red';
}

export default function SessionSummary({ questions, answers, flagged, onStartAnother, onReviewIncorrect }) {
  const totalCorrect = answers.filter(a => a.correct).length;
  const total = answers.length;
  const pct = total > 0 ? Math.round((totalCorrect / total) * 100) : 0;

  const avgTime = useMemo(() => {
    const times = answers.map(a => a.response_time_seconds).filter(t => t != null && t > 0);
    if (!times.length) return null;
    return times.reduce((s, t) => s + t, 0) / times.length;
  }, [answers]);

  // Group by subtopic
  const byTopic = useMemo(() => {
    const map = {};
    questions.forEach((q, i) => {
      const ans = answers[i];
      if (!ans) return;
      const key = q.subtopic;
      if (!map[key]) map[key] = { subtopic: q.subtopic, subject_area: q.subject_area, correct: 0, total: 0 };
      map[key].total += 1;
      if (ans.correct) map[key].correct += 1;
    });
    return Object.values(map).sort((a, b) => (a.correct / a.total) - (b.correct / b.total));
  }, [questions, answers]);

  const flaggedItems = useMemo(() => {
    return questions
      .map((q, i) => ({ q, ans: answers[i], idx: i }))
      .filter(item => flagged && flagged[item.idx]);
  }, [questions, answers, flagged]);

  const weakest = byTopic[0];
  const hasIncorrect = answers.some(a => !a.correct);

  return (
    <div className="qp-summary">
      <div className="qp-summary-score">{totalCorrect} / {total}</div>
      <div className="qp-summary-score-sub">{pct}% correct</div>
      {avgTime != null && (
        <div className="qp-summary-time">Average time per question: {fmtSecs(avgTime)}</div>
      )}

      {weakest && total > 1 && (
        <div className="qp-summary-weakest">
          Weakest topic this session: <strong>{weakest.subtopic}</strong> ({weakest.correct}/{weakest.total} correct)
        </div>
      )}

      {flaggedItems.length > 0 && (
        <div className="qp-summary-flagged">
          <div className="qp-summary-section-label">Flagged for review</div>
          {flaggedItems.map(({ q, ans, idx }) => (
            <div key={idx} className="qp-summary-flagged-row">
              <span className="qp-flag-icon">⚑</span>
              <span className="qp-summary-flagged-stem">{q.stem ? q.stem.slice(0, 80) + (q.stem.length > 80 ? '...' : '') : ''}</span>
              <span className={ans?.correct ? 'qp-summary-topic-green' : 'qp-summary-topic-red'}>
                {ans?.correct ? '✓' : '✗'}
              </span>
            </div>
          ))}
        </div>
      )}

      {byTopic.length > 0 && (
        <div className="qp-summary-topics">
          {byTopic.map(t => (
            <div key={t.subtopic} className="qp-summary-topic-row">
              <span className="qp-summary-topic-name">{t.subtopic}</span>
              <span className={`qp-summary-topic-score ${scoreClass(t.correct, t.total)}`}>
                {t.correct}/{t.total}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="qp-summary-actions">
        <button className="btn-primary" onClick={onStartAnother}>
          Start another session
        </button>
        {hasIncorrect && (
          <button className="btn-secondary qp-summary-review-btn" onClick={onReviewIncorrect}>
            Review incorrect
          </button>
        )}
      </div>
    </div>
  );
}
